import '@blocknote/core/fonts/inter.css';
import '@blocknote/mantine/style.css';
import '@mantine/core/styles.css';
import React, { useEffect } from 'react';
import { MantineProvider } from '@mantine/core';
import { BrowserRouter, Routes, Route, useLocation } from 'react-router-dom';
import { AuthProvider } from './features/auth/AuthContext';
import { ThemeProvider } from './context/ThemeContext';
import { ChannelProvider } from './context/ChannelContext';
import { ConversationProvider } from './context/ConversationContext';
import { AmpProvider } from './context/AmpContext';
import { VoiceSubscriptionProvider } from './context/VoiceSubscriptionContext';
import { StatusTemplateProvider } from './context/StatusTemplateContext';
import { InsuredDetailProvider } from './context/InsuredDetailContext';
import { PolicyDetailProvider } from './context/PolicyDetailContext';
import { DocumentPanelProvider } from './context/DocumentPanelContext';
import { FeedbackPanelProvider } from './context/FeedbackPanelContext';
import { ChannelDocumentsProvider } from './context/ChannelDocumentsContext';
import { RateQuoteLinkProvider } from './features/ratequotelink/RateQuoteLinkContext';
import { RunDetailPanelProvider } from './context/RunDetailPanelContext';
import { CarrierSessionProvider } from './context/CarrierSessionContext';
import { CommandCenterStatsProvider } from './context/CommandCenterStatsContext';
import { ArtifactsStatsProvider } from './context/ArtifactsStatsContext';
import { RoutinesStatsProvider } from './context/RoutinesStatsContext';
import { CarrierDownloadStatsProvider } from './context/CarrierDownloadStatsContext';
import { CommunicationStatsProvider } from './context/CommunicationStatsContext';
import { SubmissionCenterStatsProvider } from './context/SubmissionCenterStatsContext';
import AppShell from './app/AppShell';
import ProposalViewPage from './features/proposals/ProposalViewPage';
import { ROUTES } from './app/routes';
import { CallDetailPanelProvider } from './context/CallDetailPanelContext';
import { DialerCallDetailProvider } from './context/DialerCallDetailContext';
import { ToastProvider } from './context/ToastContext';

const ScrollToTop: React.FC = () => {
  const { pathname } = useLocation();
  useEffect(() => {
    // Walk every element inside the center panel and reset scrollTop
    const centerPanel = document.querySelector('.center-panel');
    if (centerPanel) {
      (centerPanel as HTMLElement).scrollTop = 0;
      centerPanel.querySelectorAll('*').forEach((el) => {
        const htmlEl = el as HTMLElement;
        if (htmlEl.scrollTop > 0) htmlEl.scrollTop = 0;
      });
    }
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
};

const FullApp: React.FC = () => (
  <ThemeProvider>
    <AuthProvider>
      <ChannelProvider>
        <ConversationProvider>
          <AmpProvider>
            <VoiceSubscriptionProvider>
              <StatusTemplateProvider>
                <InsuredDetailProvider>
                  <PolicyDetailProvider>
                    <DocumentPanelProvider>
                      <FeedbackPanelProvider>
                        <ChannelDocumentsProvider>
                          <RateQuoteLinkProvider>
                            <CallDetailPanelProvider>
                            <DialerCallDetailProvider>
                            <RunDetailPanelProvider>
                              <CarrierSessionProvider>
                                <ArtifactsStatsProvider>
                                <CommandCenterStatsProvider>
                                <RoutinesStatsProvider>
                                <CarrierDownloadStatsProvider>
                                <CommunicationStatsProvider>
                                <SubmissionCenterStatsProvider>
                                  <AppShell />
                                </SubmissionCenterStatsProvider>
                                </CommunicationStatsProvider>
                                </CarrierDownloadStatsProvider>
                                </RoutinesStatsProvider>
                                </CommandCenterStatsProvider>
                                </ArtifactsStatsProvider>
                              </CarrierSessionProvider>
                            </RunDetailPanelProvider>
                            </DialerCallDetailProvider>
                            </CallDetailPanelProvider>
                          </RateQuoteLinkProvider>
                        </ChannelDocumentsProvider>
                      </FeedbackPanelProvider>
                    </DocumentPanelProvider>
                  </PolicyDetailProvider>
                </InsuredDetailProvider>
              </StatusTemplateProvider>
            </VoiceSubscriptionProvider>
          </AmpProvider>
        </ConversationProvider>
      </ChannelProvider>
    </AuthProvider>
  </ThemeProvider>
);

const App: React.FC = () => {
  return (
    <MantineProvider>
      {/* Above the router/auth split so every route — including the public,
          unauthenticated proposal-view link — can show toast notifications. */}
      <ToastProvider>
        <BrowserRouter>
          <ScrollToTop />
          <Routes>
            {/* Public shareable proposal link — no auth providers needed */}
            <Route path={ROUTES.PROPOSAL_VIEW} element={<ProposalViewPage />} />
            {/* All other routes go through the full authenticated shell */}
            <Route path="*" element={<FullApp />} />
          </Routes>
        </BrowserRouter>
      </ToastProvider>
    </MantineProvider>
  );
};

export default App;
